import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, resolveImage, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import Logo from "@/components/Logo";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Switch } from "@/components/ui/switch";
import { Plus, Pencil, Trash2, LogOut, Upload, X, Loader2, ExternalLink, RefreshCw, Star } from "lucide-react";

const EMPTY = {
  brand: "", model: "", price: 0, year: new Date().getFullYear(), mileage: 0, power_hp: 0,
  fuel: "Benzin", transmission: "Automatik", body_type: "Coupé",
  description_de: "", description_en: "", description_es: "",
  images: [], featured: false, status: "available",
};

const fmtPrice = (p) => new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(p);

export default function Admin() {
  const { user, logout } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const onImport = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/vehicles/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImportResult(data);
      toast.success(`Import fertig: ${data.created} neu, ${data.updated} aktualisiert, ${data.marked_sold} als verkauft markiert`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setImporting(false);
    }
  };

  // ----- mobile.de Seller-API Sync -----
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  const onSync = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const { data } = await api.post("/admin/mobilede/sync", {}, { timeout: 300000 });
      setSyncResult(data);
      toast.success(`Sync fertig: ${data.total_ads} Inserate · ${data.imported} neu · ${data.sold} verkauft`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Sync fehlgeschlagen");
    } finally {
      setSyncing(false);
    }
  };

  // ----- Angebote (Mietwagen & Auto-Abo) selbst pflegen -----
  const [offers, setOffers] = useState([]);
  const [offerForm, setOfferForm] = useState({ section: "mietwagen", title: "", note: "", brand: "", price: "", free_km: "", image: "" });
  const [offerSaving, setOfferSaving] = useState(false);

  const loadOffers = async () => {
    try {
      const { data } = await api.get("/admin/offers");
      setOffers(data);
    } catch {
      /* ignore */
    }
  };

  const addOffer = async () => {
    if (!offerForm.title.trim()) return;
    setOfferSaving(true);
    try {
      await api.post("/admin/offers", {
        ...offerForm,
        price: offerForm.price === "" ? null : Number(offerForm.price),
        free_km: offerForm.free_km === "" ? null : Number(offerForm.free_km),
        order: offers.filter((o) => o.section === offerForm.section).length,
      });
      setOfferForm({ ...offerForm, title: "", note: "", brand: "", price: "", free_km: "", image: "" });
      toast.success("Angebot hinzugefügt");
      loadOffers();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setOfferSaving(false);
    }
  };

  const toggleOffer = async (o) => {
    await api.put(`/admin/offers/${o.id}`, {
      section: o.section, title: o.title, note: o.note || "",
      brand: o.brand || "", price: o.price ?? null, free_km: o.free_km ?? null,
      image: o.image || "",
      active: !o.active, order: o.order || 0,
    });
    loadOffers();
  };

  const deleteOffer = async (id) => {
    await api.delete(`/admin/offers/${id}`);
    toast.success("Angebot gelöscht");
    loadOffers();
  };

  // ----- Eingehende Bewerbungen -----
  const [applications, setApplications] = useState([]);
  const [expandedApp, setExpandedApp] = useState(null);

  const loadApplications = async () => {
    try {
      const { data } = await api.get("/applications");
      setApplications(data);
    } catch {
      /* ignore */
    }
  };

  const fmtDate = (iso) =>
    new Date(iso).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });

  useEffect(() => {
    if (user === false) navigate("/login");
  }, [user, navigate]);

  const load = () => api.get("/vehicles", { params: { status: "all" } }).then(({ data }) => setVehicles(data)).catch(() => {});

  // Angebot des Tages: featured-Fahrzeug per Klick festlegen (nur eins aktiv)
  const toggleFeature = async (v) => {
    try {
      const { data } = await api.post(`/vehicles/${v.id}/feature`);
      toast.success(data.featured
        ? `${v.brand} ${v.model} ist jetzt das Angebot des Tages.`
        : `${v.brand} ${v.model} ist nicht mehr das Angebot des Tages.`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };
  useEffect(() => { load(); loadOffers(); loadApplications(); }, []);

  const openNew = () => { setForm(EMPTY); setEditId(null); setDialogOpen(true); };
  const openEdit = (v) => {
    const { id, created_at, ...rest } = v;
    setForm({ ...EMPTY, ...rest });
    setEditId(id);
    setDialogOpen(true);
  };

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const urls = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        urls.push(data.url);
      }
      setForm((f) => ({ ...f, images: [...f.images, ...urls] }));
      toast.success(`${urls.length} Bild(er) hochgeladen`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (idx) => setForm((f) => ({ ...f, images: f.images.filter((_, i) => i !== idx) }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        price: Number(form.price), year: Number(form.year),
        mileage: Number(form.mileage), power_hp: Number(form.power_hp),
      };
      if (editId) await api.put(`/vehicles/${editId}`, payload);
      else await api.post("/vehicles", payload);
      toast.success("Gespeichert");
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/vehicles/${deleteId}`);
      toast.success("Gelöscht");
      setDeleteId(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  if (!user || user === false) {
    return <div className="bg-obsidian min-h-screen flex items-center justify-center text-brand-bright uppercase tracking-widest">Laden...</div>;
  }

  const inputCls = "w-full bg-obsidian border border-white/15 rounded-none focus:border-brand-light focus:outline-none text-white placeholder-zinc-600 px-3 py-2.5 text-sm";
  const labelCls = "text-xs uppercase tracking-wider text-zinc-400 mb-1.5 block";

  return (
    <div className="bg-obsidian min-h-screen" data-testid="admin-page">
      <header className="glass border-b border-white/10 sticky top-0 z-30">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
          <Logo compact />
          <div className="flex items-center gap-4">
            <a href="/" target="_blank" rel="noreferrer" className="text-zinc-400 hover:text-brand-bright transition-colors flex items-center gap-1.5 text-sm uppercase tracking-wider">
              <ExternalLink className="w-4 h-4" /> Website
            </a>
            <button onClick={() => { logout(); navigate("/login"); }} data-testid="admin-logout" className="text-zinc-400 hover:text-brand-bright transition-colors flex items-center gap-1.5 text-sm uppercase tracking-wider">
              <LogOut className="w-4 h-4" /> {t.admin.logout}
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-12">
        <div className="flex items-center justify-between mb-10">
          <div>
            <h1 className="font-heading font-black uppercase tracking-tighter text-white text-3xl md:text-4xl">{t.admin.dashboard}</h1>
            <p className="text-zinc-500 text-sm mt-1">{vehicles.length} Fahrzeuge im Bestand</p>
          </div>
          <button onClick={openNew} data-testid="admin-new-vehicle" className="inline-flex items-center gap-2 bg-brand text-black font-bold uppercase tracking-wider text-sm px-5 py-3 hover:bg-brand-hover transition-colors">
            <Plus className="w-4 h-4" /> {t.admin.newVehicle}
          </button>
        </div>

        {/* mobile.de Bestand importieren */}
        <div className="mb-8 border border-white/10 bg-obsidian-surface p-5 flex flex-col md:flex-row md:items-center gap-4" data-testid="admin-import">
          <div className="flex-1">
            <div className="text-white font-bold uppercase tracking-wider text-sm">mobile.de Bestand synchronisieren</div>
            <p className="text-zinc-500 text-xs mt-1 leading-relaxed">
              <strong className="text-zinc-300">Seller-API (empfohlen):</strong> Ein Klick holt den aktuellen Bestand direkt von mobile.de — neue Fahrzeuge kommen rein, verkaufte verschwinden automatisch.
              Alternativ: CSV-Export aus dem Händlerbereich hochladen. Erkannte Spalten: Marke, Modell, Preis, Baujahr/EZ, KM, PS, Kraftstoff, Getriebe, Bilder.
            </p>
            {importResult && (
              <p className="text-xs mt-2 text-brand-bright" data-testid="import-result">
                {importResult.created} neu · {importResult.updated} aktualisiert · {importResult.marked_sold} verkauft
                {importResult.errors?.length > 0 && <span className="text-red-400"> · {importResult.errors.length} Zeilen mit Fehlern: {importResult.errors[0]}</span>}
              </p>
            )}
            {syncResult && (
              <p className="text-xs mt-2 text-brand-bright" data-testid="sync-result">
                mobile.de Sync: {syncResult.total_ads} Inserate gelesen · {syncResult.imported} neu · {syncResult.updated} aktualisiert · {syncResult.sold} als verkauft markiert
              </p>
            )}
          </div>
          <div className="flex flex-col sm:flex-row gap-3 shrink-0">
            <button
              onClick={onSync}
              disabled={syncing}
              className="inline-flex items-center justify-center gap-2 bg-brand text-black font-bold uppercase tracking-wider text-sm px-5 py-3 hover:bg-brand-hover transition-colors disabled:opacity-50 whitespace-nowrap"
              data-testid="mobilede-sync-btn"
            >
              {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              Mit mobile.de synchronisieren
            </button>
            <label
              className="inline-flex items-center justify-center gap-2 border border-brand-light/50 text-brand-bright font-bold uppercase tracking-wider text-sm px-5 py-3 hover:bg-brand hover:text-black transition-colors cursor-pointer whitespace-nowrap"
              data-testid="import-csv-btn"
            >
              {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              CSV importieren
              <input type="file" accept=".csv,text/csv" className="hidden" onChange={onImport} disabled={importing} />
            </label>
          </div>
        </div>

        <div className="border border-white/10 overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-vehicle-table">
            <thead className="bg-obsidian-surface text-zinc-400 uppercase tracking-wider text-xs">
              <tr>
                <th className="text-left p-4">Bild</th>
                <th className="text-left p-4">Marke / Modell</th>
                <th className="text-left p-4 hidden md:table-cell">Baujahr</th>
                <th className="text-left p-4">Preis</th>
                <th className="text-left p-4 hidden md:table-cell">Status</th>
                <th className="text-right p-4">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {vehicles.map((v) => (
                <tr key={v.id} className="border-t border-white/10 hover:bg-obsidian-surface transition-colors" data-testid={`admin-row-${v.id}`}>
                  <td className="p-4">
                    <div className="w-16 h-12 bg-obsidian-surface overflow-hidden">
                      {v.images?.[0] && <img src={resolveImage(v.images[0])} alt="" className="w-full h-full object-cover" />}
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="text-white font-semibold">{v.brand}</div>
                    <div className="text-zinc-400">{v.model}</div>
                  </td>
                  <td className="p-4 text-zinc-300 hidden md:table-cell">{v.year}</td>
                  <td className="p-4 text-brand-bright font-semibold">{fmtPrice(v.price)}</td>
                  <td className="p-4 hidden md:table-cell">
                    <span className={`text-xs uppercase tracking-wider px-2 py-1 ${v.status === "sold" ? "bg-red-600/20 text-red-400" : "bg-brand/20 text-brand-bright"}`}>
                      {v.status === "sold" ? t.fuhrpark.sold : "Verfügbar"}
                    </span>
                    {v.featured && (
                      <span className="ml-2 text-xs uppercase tracking-wider px-2 py-1 bg-brand text-black font-bold" data-testid={`featured-badge-${v.id}`}>
                        Angebot des Tages
                      </span>
                    )}
                  </td>
                  <td className="p-4">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => toggleFeature(v)}
                        data-testid={`admin-feature-${v.id}`}
                        className={`p-2 transition-colors ${v.featured ? "text-brand-bright" : "text-zinc-600 hover:text-brand-bright"}`}
                        aria-label="Angebot des Tages"
                        title="Als Angebot des Tages festlegen"
                      >
                        <Star className="w-4 h-4" fill={v.featured ? "currentColor" : "none"} />
                      </button>
                      <button onClick={() => openEdit(v)} data-testid={`admin-edit-${v.id}`} className="p-2 text-zinc-400 hover:text-brand-bright transition-colors" aria-label="Bearbeiten"><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => setDeleteId(v.id)} data-testid={`admin-delete-${v.id}`} className="p-2 text-zinc-400 hover:text-red-400 transition-colors" aria-label="Löschen"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {vehicles.length === 0 && <p className="text-zinc-500 text-center py-12">Noch keine Fahrzeuge.</p>}
        </div>
      </div>

      {/* Angebote: Mietwagen & Auto-Abo selbst pflegen */}
      <div className="mt-14" data-testid="admin-offers">
        <h2 className="font-heading font-black uppercase tracking-tighter text-white text-3xl md:text-4xl mb-2">Angebote: Mietwagen & Auto-Abo</h2>
        <p className="text-zinc-500 text-sm mb-8">Diese Einträge erscheinen direkt auf den Seiten /mietwagen und /auto-abo. Hinzufügen, deaktivieren oder löschen – ohne Technik.</p>

        <div className="border border-white/10 bg-obsidian-surface p-5 mb-8 flex flex-col md:flex-row gap-4 md:items-end">
          <div className="md:w-56">
            <label className={labelCls}>Bereich</label>
            <select
              value={offerForm.section}
              onChange={(e) => setOfferForm({ ...offerForm, section: e.target.value })}
              className={inputCls}
              data-testid="offer-section"
            >
              <option value="mietwagen">Mietwagen</option>
              <option value="auto-abo">Auto-Abo</option>
            </select>
          </div>
          <div className="flex-1">
            <label className={labelCls}>Modell / Titel</label>
            <input value={offerForm.title} onChange={(e) => setOfferForm({ ...offerForm, title: e.target.value })} className={inputCls} placeholder="z. B. Porsche 911 Carrera" data-testid="offer-title" />
          </div>
          <div className="flex-1">
            <label className={labelCls}>Hinweis (optional)</label>
            <input value={offerForm.note} onChange={(e) => setOfferForm({ ...offerForm, note: e.target.value })} className={inputCls} placeholder="z. B. ab 6 Monaten" data-testid="offer-note" />
          </div>
          <div className="w-full sm:w-40">
            <label className={labelCls}>Marke (optional)</label>
            <input value={offerForm.brand} onChange={(e) => setOfferForm({ ...offerForm, brand: e.target.value })} className={inputCls} placeholder="z. B. BMW" data-testid="offer-brand" />
          </div>
          <div className="w-full sm:w-36">
            <label className={labelCls}>Rate €/Monat</label>
            <input type="number" min="0" value={offerForm.price} onChange={(e) => setOfferForm({ ...offerForm, price: e.target.value })} className={inputCls} placeholder="949" data-testid="offer-price" />
          </div>
          <div className="w-full sm:w-36">
            <label className={labelCls}>Freikm/Monat</label>
            <input type="number" min="0" value={offerForm.free_km} onChange={(e) => setOfferForm({ ...offerForm, free_km: e.target.value })} className={inputCls} placeholder="3500" data-testid="offer-freekm" />
          </div>
          <div className="flex-1 min-w-[200px]">
            <label className={labelCls}>Beispielfoto-Pfad (optional)</label>
            <input value={offerForm.image} onChange={(e) => setOfferForm({ ...offerForm, image: e.target.value })} className={inputCls} placeholder="z. B. /abo/bmw-x5.webp" data-testid="offer-image" />
          </div>
          <button
            onClick={addOffer}
            disabled={offerSaving || !offerForm.title.trim()}
            className="inline-flex items-center gap-2 bg-brand text-black font-bold uppercase tracking-wider text-sm px-5 py-3 hover:bg-brand-hover transition-colors disabled:opacity-50"
            data-testid="offer-add-btn"
          >
            {offerSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Hinzufügen
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {["mietwagen", "auto-abo"].map((sec) => (
            <div key={sec} className="border border-white/10" data-testid={`offers-${sec}`}>
              <div className="bg-obsidian-surface px-5 py-3 text-xs uppercase tracking-[0.25em] text-brand-bright border-b border-white/10">
                {sec === "mietwagen" ? "Mietwagen" : "Auto-Abo"}
              </div>
              {offers.filter((o) => o.section === sec).length === 0 && (
                <p className="text-zinc-500 text-sm p-5">Noch keine Einträge.</p>
              )}
              {offers.filter((o) => o.section === sec).map((o) => (
                <div key={o.id} className="flex items-center gap-3 px-5 py-3.5 border-b border-white/5 last:border-0" data-testid={`offer-row-${o.id}`}>
                  {o.image && (
                    <img src={o.image} alt="" className="w-16 h-10 object-cover border border-white/10 shrink-0" loading="lazy" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className={`font-semibold ${o.active ? "text-white" : "text-zinc-600 line-through"}`}>
                      {o.brand ? `${o.brand} ` : ""}{o.title}
                    </div>
                    <div className="text-zinc-500 text-xs">
                      {o.price != null && <span className="text-brand-bright font-semibold">{new Intl.NumberFormat("de-DE").format(o.price)} €/Monat</span>}
                      {o.free_km != null && <span> · {new Intl.NumberFormat("de-DE").format(o.free_km)} Freikm</span>}
                      {o.note && <span> · {o.note}</span>}
                    </div>
                  </div>
                  <button onClick={() => toggleOffer(o)} className={`text-[10px] uppercase tracking-wider px-2.5 py-1.5 border transition-colors ${o.active ? "border-brand-light/40 text-brand-bright" : "border-white/15 text-zinc-500"}`} data-testid={`offer-toggle-${o.id}`}>
                    {o.active ? "Aktiv" : "Inaktiv"}
                  </button>
                  <button onClick={() => deleteOffer(o.id)} className="p-2 text-zinc-500 hover:text-red-400 transition-colors" aria-label="Löschen" data-testid={`offer-delete-${o.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Eingehende Bewerbungen */}
      <div className="mt-14" data-testid="admin-applications">
        <h2 className="font-heading font-black uppercase tracking-tighter text-white text-3xl md:text-4xl mb-2">Bewerbungen</h2>
        <p className="text-zinc-500 text-sm mb-8">Eingehende Bewerbungen aus dem Karriere-Bereich – zusätzlich erhältst du jede Bewerbung per E-Mail.</p>

        {applications.length === 0 ? (
          <p className="text-zinc-500 text-sm border border-white/10 p-5">Noch keine Bewerbungen eingegangen.</p>
        ) : (
          <div className="border border-white/10">
            {applications.map((a) => (
              <div key={a.id} className="border-b border-white/5 last:border-0" data-testid={`application-${a.id}`}>
                <button
                  onClick={() => setExpandedApp(expandedApp === a.id ? null : a.id)}
                  className="w-full flex flex-col md:flex-row md:items-center gap-2 md:gap-6 px-5 py-4 text-left hover:bg-white/[0.02] transition-colors"
                  data-testid={`application-toggle-${a.id}`}
                >
                  <span className="text-brand-bright text-xs font-bold uppercase tracking-wider md:w-56 shrink-0">{a.position}</span>
                  <span className="text-white font-semibold flex-1">{a.first_name} {a.last_name}</span>
                  <span className="text-zinc-500 text-xs shrink-0">{fmtDate(a.created_at)}</span>
                </button>
                {expandedApp === a.id && (
                  <div className="px-5 pb-5 pt-1 text-sm text-zinc-300 space-y-2 bg-obsidian-surface/50" data-testid={`application-detail-${a.id}`}>
                    <p><span className="text-zinc-500">E-Mail:</span> <a href={`mailto:${a.email}`} className="text-brand-bright hover:underline">{a.email}</a></p>
                    {a.phone && <p><span className="text-zinc-500">Telefon:</span> {a.phone}</p>}
                    {a.city && <p><span className="text-zinc-500">Wohnort:</span> {a.city}</p>}
                    {a.start_date && <p><span className="text-zinc-500">Eintrittstermin:</span> {a.start_date}</p>}
                    {a.message && <p className="pt-2 border-t border-white/5"><span className="text-zinc-500">Nachricht:</span><br />{a.message}</p>}
                    <div className="flex flex-wrap gap-3 pt-2">
                      {a.cv_url && (
                        <a href={resolveImage(a.cv_url)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs border border-brand-light/40 text-brand-bright px-3 py-2 hover:bg-brand hover:text-black transition-colors" data-testid={`application-cv-${a.id}`}>
                          Lebenslauf öffnen
                        </a>
                      )}
                      {(a.document_urls || []).map((u, i) => (
                        <a key={u} href={resolveImage(u)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs border border-white/15 text-zinc-300 px-3 py-2 hover:border-brand-light hover:text-brand-bright transition-colors">
                          Dokument {i + 1}
                        </a>
                      ))}
                      {!a.cv_url && !(a.document_urls || []).length && (
                        <span className="text-zinc-600 text-xs">Keine Unterlagen hochgeladen</span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-obsidian-elevated border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="admin-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase tracking-tight text-2xl">
              {editId ? t.admin.edit : t.admin.newVehicle}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div><label className={labelCls}>Marke</label><input value={form.brand} onChange={(e) => setField("brand", e.target.value)} className={inputCls} data-testid="field-brand" /></div>
              <div><label className={labelCls}>Modell</label><input value={form.model} onChange={(e) => setField("model", e.target.value)} className={inputCls} data-testid="field-model" /></div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div><label className={labelCls}>Preis (€)</label><input type="number" value={form.price} onChange={(e) => setField("price", e.target.value)} className={inputCls} data-testid="field-price" /></div>
              <div><label className={labelCls}>Baujahr</label><input type="number" value={form.year} onChange={(e) => setField("year", e.target.value)} className={inputCls} data-testid="field-year" /></div>
              <div><label className={labelCls}>KM</label><input type="number" value={form.mileage} onChange={(e) => setField("mileage", e.target.value)} className={inputCls} data-testid="field-mileage" /></div>
              <div><label className={labelCls}>PS</label><input type="number" value={form.power_hp} onChange={(e) => setField("power_hp", e.target.value)} className={inputCls} data-testid="field-power" /></div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div><label className={labelCls}>Kraftstoff</label><input value={form.fuel} onChange={(e) => setField("fuel", e.target.value)} className={inputCls} /></div>
              <div><label className={labelCls}>Getriebe</label><input value={form.transmission} onChange={(e) => setField("transmission", e.target.value)} className={inputCls} /></div>
              <div><label className={labelCls}>Karosserie</label><input value={form.body_type} onChange={(e) => setField("body_type", e.target.value)} className={inputCls} /></div>
            </div>

            <div><label className={labelCls}>Beschreibung (DE)</label><textarea rows={2} value={form.description_de} onChange={(e) => setField("description_de", e.target.value)} className={inputCls + " resize-none"} data-testid="field-desc-de" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className={labelCls}>Beschreibung (EN)</label><textarea rows={2} value={form.description_en} onChange={(e) => setField("description_en", e.target.value)} className={inputCls + " resize-none"} /></div>
              <div><label className={labelCls}>Beschreibung (ES)</label><textarea rows={2} value={form.description_es} onChange={(e) => setField("description_es", e.target.value)} className={inputCls + " resize-none"} /></div>
            </div>

            <div>
              <label className={labelCls}>{t.admin.uploadImage}</label>
              <div className="flex flex-wrap gap-3 mb-3">
                {form.images.map((im, i) => (
                  <div key={i} className="relative w-24 h-16 border border-white/10 overflow-hidden group">
                    <img src={resolveImage(im)} alt="" className="w-full h-full object-cover" />
                    <button onClick={() => removeImage(i)} className="absolute top-0 right-0 bg-black/70 p-1 text-white hover:text-red-400" data-testid={`remove-img-${i}`}><X className="w-3 h-3" /></button>
                  </div>
                ))}
                <label className="w-24 h-16 border border-dashed border-white/20 flex items-center justify-center cursor-pointer hover:border-brand-light transition-colors" data-testid="upload-image-btn">
                  {uploading ? <Loader2 className="w-5 h-5 text-brand-bright animate-spin" /> : <Upload className="w-5 h-5 text-zinc-500" />}
                  <input type="file" accept="image/*" multiple className="hidden" onChange={onUpload} />
                </label>
              </div>
              <input value={form.images.join(", ")} onChange={(e) => setField("images", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} placeholder="Oder Bild-URLs (kommagetrennt)" className={inputCls} data-testid="field-image-urls" />
            </div>

            <div className="flex items-center gap-8 pt-2">
              <label className="flex items-center gap-3 cursor-pointer">
                <Switch checked={form.featured} onCheckedChange={(v) => setField("featured", v)} data-testid="field-featured" />
                <span className="text-sm text-zinc-300 uppercase tracking-wider">Featured</span>
              </label>
              <label className="flex items-center gap-3 cursor-pointer">
                <Switch checked={form.status === "sold"} onCheckedChange={(v) => setField("status", v ? "sold" : "available")} data-testid="field-sold" />
                <span className="text-sm text-zinc-300 uppercase tracking-wider">{t.fuhrpark.sold}</span>
              </label>
            </div>
          </div>

          <DialogFooter>
            <button onClick={() => setDialogOpen(false)} className="px-5 py-2.5 border border-white/20 text-white uppercase tracking-wider text-sm hover:border-white/40 transition-colors">{t.admin.cancel}</button>
            <button onClick={save} disabled={saving} data-testid="admin-save" className="px-5 py-2.5 bg-brand text-black font-bold uppercase tracking-wider text-sm hover:bg-brand-hover transition-colors flex items-center gap-2 disabled:opacity-50">
              {saving && <Loader2 className="w-4 h-4 animate-spin" />} {t.admin.save}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent className="bg-obsidian-elevated border-white/10 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading uppercase tracking-tight">{t.admin.confirmDelete}</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">Diese Aktion kann nicht rückgängig gemacht werden.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-white/20 text-white hover:bg-white/5">{t.admin.cancel}</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} data-testid="confirm-delete" className="bg-red-600 text-white hover:bg-red-700">{t.admin.delete}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
